import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sun, Moon, Eye, EyeOff, Users, BarChart3, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { seedApi } from '../services/api';

const KARYA_LOGO = "/karya-logo.png?v=3";
const HERO_IMAGE = "/login-hero.jpg";

export default function LoginPage() {
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await seedApi.seed();
      toast.success(response.data.message);
    } catch (error) {
      if (error.response?.data?.message?.includes('already seeded')) {
        toast.info('Demo data already exists');
      } else {
        toast.error('Failed to seed data');
      }
    } finally {
      setSeeding(false);
    }
  };

  const handleResetData = async () => {
    if (!window.confirm('This will WIPE all existing data and re-seed fresh Karya demo data. Continue?')) {
      return;
    }
    setResetting(true);
    try {
      const response = await seedApi.reset();
      toast.success(response.data.message || 'Demo data reset successfully');
    } catch (error) {
      toast.error('Failed to reset demo data');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0f2744]">
      {/* Left side - Hero image with branding */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        {/* Background hero image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        {/* Brand-tone overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f2744]/95 via-[#1a365d]/85 to-[#0d4f4a]/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f2744]/70 via-transparent to-transparent" />

        {/* Decorative glow */}
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#2dd4bf]/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -right-20 w-[28rem] h-[28rem] bg-[#2dd4bf]/10 rounded-full blur-3xl" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 text-white w-full">
          {/* Logo in a glass card */}
          <div className="inline-flex items-center gap-3 self-start backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl px-5 py-3 shadow-2xl shadow-black/20">
            <img
              src={KARYA_LOGO}
              alt="Karya HRMS"
              className="h-10 w-auto object-contain"
              style={{ filter: 'brightness(0) invert(1)' }}
              data-testid="company-logo"
            />
          </div>

          {/* Hero text */}
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-[#2dd4bf]/15 border border-[#2dd4bf]/30 backdrop-blur-sm">
              <span className="h-2 w-2 rounded-full bg-[#2dd4bf] animate-pulse" />
              <span className="text-xs font-semibold tracking-wider uppercase text-[#7ff0dc]">Enterprise HRMS Platform</span>
            </div>
            <h1 className="text-5xl xl:text-6xl font-bold leading-[1.05] mb-6 tracking-tight">
              People-first<br />
              <span className="bg-gradient-to-r from-[#2dd4bf] to-[#7ff0dc] bg-clip-text text-transparent">
                HR, reimagined.
              </span>
            </h1>
            <p className="text-lg text-white/75 max-w-md mb-10 leading-relaxed">
              One platform for payroll, attendance, leaves, performance and compliance — built for modern enterprises.
            </p>

            {/* Feature highlights */}
            <div className="grid grid-cols-1 gap-3 max-w-md">
              <div className="flex items-center gap-4 p-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-[#2dd4bf]/20 flex items-center justify-center flex-shrink-0">
                  <Users className="h-5 w-5 text-[#2dd4bf]" />
                </div>
                <span className="text-sm text-white/90 font-medium">Employee & Payroll Management</span>
              </div>
              <div className="flex items-center gap-4 p-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-[#2dd4bf]/20 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="h-5 w-5 text-[#2dd4bf]" />
                </div>
                <span className="text-sm text-white/90 font-medium">Real-time Analytics & Reports</span>
              </div>
              <div className="flex items-center gap-4 p-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-[#2dd4bf]/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="h-5 w-5 text-[#2dd4bf]" />
                </div>
                <span className="text-sm text-white/90 font-medium">Secure & Compliance Ready</span>
              </div>
            </div>
          </div>

          {/* Footer line */}
          <div className="text-xs text-white/50 tracking-wider">
            © {new Date().getFullYear()} Karya HRMS · Built for modern teams
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-16 xl:px-24 bg-background">
        <div className="mx-auto w-full max-w-sm">
          {/* Theme toggle */}
          <div className="absolute top-6 right-6">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleTheme}
              className="rounded-full"
              data-testid="login-theme-toggle"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
          </div>

          {/* Logo - visible on mobile */}
          <div className="mb-8 flex justify-center lg:hidden">
            <img 
              src={KARYA_LOGO} 
              alt="Karya HRMS" 
              className="h-14 w-auto object-contain"
              data-testid="mobile-logo"
            />
          </div>

          {/* Welcome text */}
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-bold text-foreground">Welcome back</h2>
            <p className="text-muted-foreground mt-2">Sign in to continue to Karya HRMS</p>
          </div>

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 px-4 rounded-xl border-2 focus:border-[#2dd4bf] transition-colors"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 px-4 pr-12 rounded-xl border-2 focus:border-[#2dd4bf] transition-colors"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 rounded-xl bg-gradient-to-r from-[#1a365d] to-[#2dd4bf] hover:from-[#0f2744] hover:to-[#14b8a6] text-white text-base font-semibold"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Demo credentials */}
          <div className="mt-10 p-5 rounded-2xl bg-gradient-to-br from-[#1a365d]/5 to-[#2dd4bf]/5 border border-[#2dd4bf]/20">
            <p className="text-sm font-semibold mb-3 text-foreground">Demo Credentials</p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between p-2 rounded-lg bg-background/50">
                <span className="text-muted-foreground">HR Manager</span>
                <code className="text-xs bg-background px-2 py-1 rounded font-mono">hr@demo.karya.com</code>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-background/50">
                <span className="text-muted-foreground">Employee</span>
                <code className="text-xs bg-background px-2 py-1 rounded font-mono">john@demo.karya.com</code>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Password: demo123</p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-4 w-full rounded-lg border-[#2dd4bf]/30 hover:bg-[#2dd4bf]/10"
              onClick={handleSeedData}
              disabled={seeding}
              data-testid="seed-data-btn"
            >
              {seeding ? 'Setting up...' : 'Initialize Demo Data'}
            </Button>
            <button
              type="button"
              className="mt-2 w-full text-[11px] text-muted-foreground hover:text-destructive transition-colors"
              onClick={handleResetData}
              disabled={resetting}
              data-testid="reset-data-btn"
            >
              {resetting ? 'Resetting…' : 'Reset & re-seed demo data'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
